<template>
  <div class="feedback-toast" v-if="localVisible" :class="[type, position]">
    <div class="toast-content">
      <div class="toast-icon" @click="localVisible = false">{{ getIcon() }}</div>
      <div class="toast-message">{{ message }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from 'vue'

interface Props {
  message: string
  type?: 'success' | 'error' | 'warning' | 'info'
  duration?: number
  visible?: boolean
  position?: 'top-right' | 'top-center' | 'top-left' | 'center' | 'bottom-right' | 'bottom-center' | 'bottom-left'
}

const props = withDefaults(defineProps<Props>(), {
  type: 'info',
  duration: 3000,
  visible: false,
  position: 'top-right'
})

const emit = defineEmits<{
  'update:visible': [value: boolean]
  close: []
}>()

// 本地可见性状态，用于处理自动关闭
const localVisible = ref(props.visible)

// 监听props.visible变化，同步到localVisible
watch(
  () => props.visible,
  (newVal) => {
    localVisible.value = newVal
    if (newVal) {
      startTimer()
    }
  }
)

// 监听localVisible变化，通知父组件
watch(localVisible, (newVal) => {
  emit('update:visible', newVal)
  if (!newVal) {
    emit('close')
  }
})

// 定时器
let timer: ReturnType<typeof setTimeout> | null = null

// 启动定时器
const startTimer = () => {
  if (timer) {
    clearTimeout(timer)
  }
  if (props.duration > 0) {
    timer = setTimeout(() => {
      localVisible.value = false
      timer = null
    }, props.duration)
  }
}

// 获取图标
const getIcon = () => {
  switch (props.type) {
    case 'success':
      return '✓'
    case 'error':
      return '✗'
    case 'warning':
      return '⚠'
    default:
      return 'i'
  }
}

// 组件挂载时，如果初始可见则启动定时器
onMounted(() => {
  if (localVisible.value) {
    startTimer()
  }
})

// 组件卸载时清理定时器
onUnmounted(() => {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
})

// 导出组件
</script>

<style lang="scss" scoped>
.feedback-toast {
  position: fixed;
  z-index: 3000;
  padding: 14px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 200px;
}

// 默认位置（右上角）
.feedback-toast.top-right {
  top: 20px;
  right: 20px;
  animation: slideInRight 0.3s ease-out;
}

// 顶部居中
.feedback-toast.top-center {
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  animation: slideInTop 0.3s ease-out;
}

// 左上角
.feedback-toast.top-left {
  top: 20px;
  left: 20px;
  animation: slideInLeft 0.3s ease-out;
}

// 居中
.feedback-toast.center {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: fadeIn 0.3s ease-out;
}

// 右下角
.feedback-toast.bottom-right {
  bottom: 20px;
  right: 20px;
  animation: slideInRight 0.3s ease-out;
}

// 底部居中
.feedback-toast.bottom-center {
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  animation: slideInBottom 0.3s ease-out;
}

// 左下角
.feedback-toast.bottom-left {
  bottom: 20px;
  left: 20px;
  animation: slideInLeft 0.3s ease-out;
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInTop {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-100%);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

@keyframes slideInBottom {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(100%);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.feedback-toast.success {
  background-color: #f0f9eb;
  border-left: 4px solid #67c23a;
  color: #3f8600;
}

.feedback-toast.error {
  background-color: #fef0f0;
  border-left: 4px solid #f56c6c;
  color: #cf1322;
}

.feedback-toast.warning {
  background-color: #fdf6ec;
  border-left: 4px solid #e6a23c;
  color: #d46b08;
}

.feedback-toast.info {
  background-color: #ecf5ff;
  border-left: 4px solid #409eff;
  color: #1890ff;
}

.toast-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toast-icon {
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
}

.toast-message {
  font-size: 14px;
  line-height: 1.4;
  max-width: 300px;
  word-wrap: break-word;
}
</style>
